import React, { useEffect, useState, useMemo } from "react";
import Sidebar from "../components/Sidebar";
import { Bars3Icon } from "@heroicons/react/24/outline";
import SearchableDropdown from "../components/SearchableDropdown";
import TableHeaderFilter from "../components/TableHeaderfilter";
import ExcelExport from "../components/ExcelExport";
import axios from "axios";
import { SERVER_URL } from "../config";

const statusTabs = ["PENDING", "APPROVED", "REJECTED", "PARTIALLY APPROVED"];

/* ── status normalizer ─────────────────────────────────────────────────────── */
const normalizeStatus = (status) => {
    if (!status) return "";
    let s = status.toUpperCase().trim();
    if (s === "PARTIAL APPROVED") s = "PARTIALLY APPROVED";
    return s;
};

/* ── status badge ──────────────────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
    const s = normalizeStatus(status);
    const map = {
        PENDING:              "bg-amber-50 text-amber-700 border border-amber-200",
        APPROVED:             "bg-emerald-50 text-emerald-700 border border-emerald-200",
        REJECTED:             "bg-rose-50 text-rose-700 border border-rose-200",
        "PARTIALLY APPROVED": "bg-blue-50 text-blue-700 border border-blue-200",
    };
    return (
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase ${map[s] || "bg-slate-100 text-slate-600 border border-slate-200"}`}>
            {s}
        </span>
    );
};

/* ── summary stat card ─────────────────────────────────────────────────────── */
const StatCard = ({ label, value, colorClass, dotClass }) => (
    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${colorClass}`}>
        <span className={`w-2 h-2 rounded-full ${dotClass}`} />
        {label} <span className="font-extrabold">{value}</span>
    </div>
);

/* ── tab config ────────────────────────────────────────────────────────────── */
const TAB_STYLES = {
    PENDING:              { active: "bg-amber-50 text-amber-700 border-amber-300",     idle: "text-slate-500 border-slate-200 hover:bg-amber-50 hover:text-amber-700" },
    APPROVED:             { active: "bg-emerald-50 text-emerald-700 border-emerald-300", idle: "text-slate-500 border-slate-200 hover:bg-emerald-50 hover:text-emerald-700" },
    REJECTED:             { active: "bg-rose-50 text-rose-700 border-rose-300",        idle: "text-slate-500 border-slate-200 hover:bg-rose-50 hover:text-rose-700" },
    "PARTIALLY APPROVED": { active: "bg-blue-50 text-blue-700 border-blue-300",        idle: "text-slate-500 border-slate-200 hover:bg-blue-50 hover:text-blue-700" },
};

/* ── shared input style ────────────────────────────────────────────────────── */
const inputStyle =
    "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 h-10 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors";

/* ════════════════════════════════════════════════════════════════════════════ */
const AdminExpenseApproval = () => {
    const currentDate = new Date();

    const [activeTab, setActiveTab]               = useState("PENDING");
    const [selectedMonth, setSelectedMonth]       = useState(currentDate.getMonth() + 1);
    const [selectedYear, setSelectedYear]         = useState(currentDate.getFullYear());
    const [expenseData, setExpenseData]           = useState([]);
    const [popupOpen, setPopupOpen]               = useState(false);
    const [employeeDetails, setEmployeeDetails]   = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [sidebarOpen, setSidebarOpen]           = useState(false);
    const [nameFilter, setNameFilter]             = useState([]);
    const [idFilter, setIdFilter]                 = useState([]);
    const [months, setMonths]                     = useState([]);
    const [years, setYears]                       = useState([]);

    const loggedInUser = JSON.parse(localStorage.getItem("user"));
    const adminId = loggedInUser?.user_id || 0;

    /* ---------- body scroll lock when popup open ---------- */
    useEffect(() => {
        document.body.style.overflow = popupOpen ? "hidden" : "auto";
        return () => { document.body.style.overflow = "auto"; };
    }, [popupOpen]);

    /* ---------- fetch filter options ---------- */
    useEffect(() => {
        axios.get(`${SERVER_URL}/Rahul/adminExpenseHistory.php`).then(res => {
            setMonths(res.data.months || []);
            setYears(res.data.years || []);
        });
    }, []);

    /* ---------- fetch data ---------- */
    useEffect(() => { fetchMonthData(); }, [selectedMonth, selectedYear]);

    const fetchMonthData = () => {
        axios
            .post(`${SERVER_URL}/Rahul/adminExpenseHistory.php`, { month: selectedMonth, year: selectedYear })
            .then(res => setExpenseData(res.data || []));
    };

    /* ---------- summary ---------- */
    const summaryData = useMemo(() => {
        const grouped = {};
        expenseData.forEach(item => {
            const itemStatus = normalizeStatus(item.status);
            const tabStatus  = normalizeStatus(activeTab);
            if (itemStatus !== tabStatus) return;
            if (!grouped[item.employee_id]) {
                grouped[item.employee_id] = {
                    employee_id: item.employee_id,
                    employee_name: item.employee_name,
                    total_amount: 0,
                    status: itemStatus
                };
            }
            grouped[item.employee_id].total_amount += Number(item.amount || 0);
        });
        return Object.values(grouped);
    }, [expenseData, activeTab]);

    /* ---------- filter options ---------- */
    const employeeNameOptions = [
        ...new Map(summaryData.map(item => [item.employee_name, { label: item.employee_name, value: item.employee_name }])).values()
    ];
    const employeeIdOptions = [
        ...new Map(summaryData.map(item => [item.employee_id, { label: item.employee_id, value: item.employee_id }])).values()
    ];

    /* ---------- apply filter ---------- */
    const filteredSummary = summaryData.filter(row => {
        const matchName = nameFilter.length === 0 || nameFilter.includes(row.employee_name);
        const matchId   = idFilter.length   === 0 || idFilter.includes(row.employee_id);
        return matchName && matchId;
    });

    /* ---------- tab counts ---------- */
    const tabCounts = useMemo(() => {
        const counts = {};
        statusTabs.forEach(tab => {
            counts[tab] = expenseData.filter(item => normalizeStatus(item.status) === normalizeStatus(tab)).length;
        });
        return counts;
    }, [expenseData]);

    /* ---------- row click ---------- */
    const handleRowClick = row => {
        setSelectedEmployee(row);
        const history = expenseData.filter(
            item => item.employee_id === row.employee_id &&
                    normalizeStatus(item.status) === normalizeStatus(activeTab)
        );
        setEmployeeDetails(history);
        setPopupOpen(true);
    };

    /* ---------- approve / reject all ---------- */
    const updateAllRows = (status) => {
        const ids = employeeDetails.map(item => item.id);
        axios
            .post(`${SERVER_URL}/Rahul/updateExpenseStatus.php`, { ids, status, employee_id: adminId })
            .then(() => { fetchMonthData(); setPopupOpen(false); });
    };

    /* ---------- totals ---------- */
    const partialTotal   = employeeDetails.reduce((s, i) => s + Number(i.paid_amount || 0), 0);
    const remainingTotal = employeeDetails.reduce((s, i) => s + (Number(i.amount || 0) - Number(i.paid_amount || 0)), 0);

    /* ---------- summary stats for active tab ---------- */
    const summaryStats = useMemo(() => ({
        total:   filteredSummary.length,
        amount:  filteredSummary.reduce((s, r) => s + Number(r.total_amount || 0), 0),
    }), [filteredSummary]);

    /* ---------- excel columns ---------- */
    const summaryColumns = [
        { header: "Employee Name", key: "employee_name", type: "text" },
        { header: "Employee ID",   key: "employee_id",   type: "text" },
        { header: "Total Amount",  key: "total_amount",  type: "number" },
        { header: "Status",        key: "status",        type: "text" },
    ];
    const detailColumns = [
        { header: "Date",        key: "date",         type: "text" },
        { header: "Type",        key: "expense_type", type: "text" },
        { header: "Amount",      key: "amount",       type: "number" },
        { header: "Paid Amount", key: "paid_amount",  type: "number" },
        { header: "Status",      key: "status",       type: "text" },
    ];

    /* ── RowAction ───────────────────────────────────────────────────────── */
    const RowAction = ({ item, adminId }) => {
        const [open, setOpen]                   = useState(false);
        const [mode, setMode]                   = useState("menu");
        const [partialAmount, setPartialAmount] = useState("");

        const closePopup = () => { setOpen(false); setMode("menu"); setPartialAmount(""); };

        const updateRow = (statusType, enteredAmount = 0) => {
            let previousPaid = Number(item.paid_amount || 0);
            let claimAmount  = Number(item.amount || 0);
            if (statusType === "PARTIALLY APPROVED" && Number(enteredAmount) < 0) {
                alert("Partial approved amount cannot be negative");
                return;
            }
            let finalPaidAmount = previousPaid;
            let finalStatus     = statusType;
            if (statusType === "APPROVED") finalPaidAmount = claimAmount;
            if (statusType === "PARTIALLY APPROVED") {
                finalPaidAmount = previousPaid + Number(enteredAmount || 0);
                if (finalPaidAmount >= claimAmount) { finalPaidAmount = claimAmount; finalStatus = "APPROVED"; }
            }
            axios
                .post(`${SERVER_URL}/Rahul/updateExpenseStatus.php`, { id: item.id, status: finalStatus, paid_amount: finalPaidAmount, employee_id: adminId })
                .then(() => { fetchMonthData(); closePopup(); });
        };

        return (
            <>
                <button
                    onClick={() => setOpen(true)}
                    className="px-3 py-1 rounded-lg text-xs font-semibold text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors"
                >
                    Action
                </button>

                {open && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center"
                        style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(6px)" }}>
                        <div className="w-72 rounded-2xl p-5 border border-slate-200 shadow-2xl bg-white space-y-3">
                            <h3 className="text-sm font-bold text-slate-800 mb-1">Update Status</h3>

                            {mode === "menu" && (
                                <div className="flex flex-col gap-2">
                                    <button onClick={() => updateRow("APPROVED")}
                                        className="w-full py-2 rounded-xl text-xs font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors">
                                        ✓ Approve
                                    </button>
                                    <button onClick={() => updateRow("REJECTED")}
                                        className="w-full py-2 rounded-xl text-xs font-semibold text-rose-700 border border-rose-200 bg-rose-50 hover:bg-rose-100 transition-colors">
                                        ✕ Reject
                                    </button>
                                    <button onClick={() => setMode("partial")}
                                        className="w-full py-2 rounded-xl text-xs font-semibold text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors">
                                        ◑ Partial Approve
                                    </button>
                                    <button onClick={closePopup}
                                        className="w-full py-2 rounded-xl text-xs font-semibold text-slate-500 border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors">
                                        Cancel
                                    </button>
                                </div>
                            )}

                            {mode === "partial" && (
                                <div className="flex flex-col gap-2">
                                    <input
                                        type="number"
                                        placeholder="Enter amount"
                                        value={partialAmount}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === "" || Number(val) >= 0) setPartialAmount(val);
                                        }}
                                        className={inputStyle}
                                    />
                                    <button onClick={() => updateRow("PARTIALLY APPROVED", partialAmount)}
                                        className="w-full py-2 rounded-xl text-xs font-bold text-white transition-all"
                                        style={{ background: "linear-gradient(135deg, #1e40af, #0369a1)" }}>
                                        Submit
                                    </button>
                                    <button onClick={() => setMode("menu")}
                                        className="w-full py-2 rounded-xl text-xs font-semibold text-slate-500 border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors">
                                        Back
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </>
        );
    };

    /* ════════════════════════════════════════════════════════════════════════
       JSX
    ════════════════════════════════════════════════════════════════════════ */
    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
                * { box-sizing: border-box; }
                body { font-family: 'Inter', sans-serif; }
                .exp-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
                .exp-scrollbar::-webkit-scrollbar-track { background: rgba(15,23,42,0.05); border-radius:10px; }
                .exp-scrollbar::-webkit-scrollbar-thumb { background: rgba(15,23,42,0.18); border-radius:10px; }
                .exp-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(15,23,42,0.32); }
            `}</style>

            {/* PAGE WRAPPER */}
            <div className="min-h-screen w-full font-[Inter]" style={{ background: "#f0f4f8" }}>

                {/* Subtle top accent line */}
                <div className="fixed top-0 left-0 right-0 h-0.5 z-50"
                    style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }} />

                <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} onLogout={() => { localStorage.clear(); }} />

                {/* ── STICKY HEADER ── */}
                <div className="sticky top-0 z-20 border-b border-slate-200"
                    style={{ background: "#ffffff", boxShadow: "0 1px 4px rgba(15,23,42,0.08)" }}>
                    <div className="px-7 py-3.5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors border-0"
                            >
                                <Bars3Icon className="h-5 w-5" />
                            </button>
                            <div className="flex items-center gap-2.5">
                                <div className="w-1 h-6 rounded-full"
                                    style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                                <h1 className="text-lg font-bold m-0 tracking-tight text-slate-800">
                                    Expense Approval
                                </h1>
                            </div>
                        </div>
                        <ExcelExport
                            data={filteredSummary}
                            columns={summaryColumns}
                            fileName={`Expense_Summary_${selectedMonth}_${selectedYear}.xlsx`}
                            reportTitle="Expense Summary Report"
                            reportFilters={{ month: selectedMonth, year: selectedYear }}
                        />
                    </div>
                </div>

                {/* ── MAIN CONTENT ── */}
                <div className="relative z-10 p-5 flex flex-col gap-4">

                    {/* ── FILTER BAR ── */}
                    <div className="rounded-xl px-5 py-4 bg-white border border-slate-200"
                        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">

                            {/* Tabs */}
                            <div className="flex flex-wrap gap-2">
                                {statusTabs.map((tab) => {
                                    const isActive = activeTab === tab;
                                    const style = TAB_STYLES[tab];
                                    return (
                                        <button
                                            key={tab}
                                            onClick={() => setActiveTab(tab)}
                                            className={`px-4 py-1.5 rounded-lg text-xs font-semibold border transition-all duration-200 ${isActive ? style.active : style.idle}`}
                                        >
                                            {tab}
                                            {tabCounts[tab] > 0 && (
                                                <span className="ml-1.5 text-[10px] font-bold opacity-60">
                                                    ({tabCounts[tab]})
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Month / Year dropdowns */}
                            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto lg:w-64">
                                <SearchableDropdown
                                    options={months}
                                    value={selectedMonth}
                                    onChange={setSelectedMonth}
                                    placeholder="Month"
                                />
                                <SearchableDropdown
                                    options={years}
                                    value={selectedYear}
                                    onChange={setSelectedYear}
                                    placeholder="Year"
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── SUMMARY STAT CARDS ── */}
                    <div className="flex flex-wrap gap-2">
                        <StatCard
                            label="Total Employees"
                            value={summaryStats.total}
                            colorClass="bg-blue-50 text-blue-700 border border-blue-200"
                            dotClass="bg-blue-500"
                        />
                        <StatCard
                            label="Total Amount"
                            value={`₹${summaryStats.amount.toFixed(2)}`}
                            colorClass="bg-slate-100 text-slate-700 border border-slate-200"
                            dotClass="bg-slate-500"
                        />
                        {activeTab === "PENDING" && (
                            <StatCard
                                label="Pending"
                                value={tabCounts["PENDING"]}
                                colorClass="bg-amber-50 text-amber-700 border border-amber-200"
                                dotClass="bg-amber-500"
                            />
                        )}
                        {activeTab === "APPROVED" && (
                            <StatCard
                                label="Approved"
                                value={tabCounts["APPROVED"]}
                                colorClass="bg-emerald-50 text-emerald-700 border border-emerald-200"
                                dotClass="bg-emerald-500"
                            />
                        )}
                        {activeTab === "REJECTED" && (
                            <StatCard
                                label="Rejected"
                                value={tabCounts["REJECTED"]}
                                colorClass="bg-rose-50 text-rose-700 border border-rose-200"
                                dotClass="bg-rose-500"
                            />
                        )}
                        {activeTab === "PARTIALLY APPROVED" && (
                            <StatCard
                                label="Partially Approved"
                                value={tabCounts["PARTIALLY APPROVED"]}
                                colorClass="bg-blue-50 text-blue-700 border border-blue-200"
                                dotClass="bg-blue-500"
                            />
                        )}
                    </div>

                    {/* ── SUMMARY TABLE ── */}
                    <div className="rounded-xl overflow-hidden bg-white border border-slate-200"
                        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                        <div className="overflow-x-auto overflow-y-auto max-h-[520px] exp-scrollbar">
                            <table className="min-w-full text-sm min-w-[600px]">
                                <thead className="sticky top-0 z-10" style={{ background: "#f8fafc" }}>
                                    <tr>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <TableHeaderFilter
                                                label="Employee Name"
                                                options={employeeNameOptions}
                                                selectedValues={nameFilter}
                                                onChange={setNameFilter}
                                                fullWidth
                                            />
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <TableHeaderFilter
                                                label="Employee ID"
                                                options={employeeIdOptions}
                                                selectedValues={idFilter}
                                                onChange={setIdFilter}
                                                fullWidth
                                            />
                                        </th>
                                        <th className="p-3 text-center border-b border-slate-200">
                                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Amount</span>
                                        </th>
                                        <th className="p-3 text-center border-b border-slate-200">
                                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredSummary.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="text-center p-8 text-slate-400 text-sm">No data found.</td>
                                        </tr>
                                    ) : (
                                        filteredSummary.map((row, i) => (
                                            <tr
                                                key={i}
                                                onClick={() => handleRowClick(row)}
                                                className="border-t border-slate-100 cursor-pointer transition-colors hover:bg-blue-50/60 text-center"
                                            >
                                                <td className="p-3 text-slate-800 text-xs font-medium text-left">{row.employee_name}</td>
                                                <td className="p-3 text-slate-500 text-xs font-medium text-left">{row.employee_id}</td>
                                                <td className="p-3 text-slate-700 text-xs">{Number(row.total_amount).toFixed(2)}</td>
                                                <td className="p-3"><StatusBadge status={row.status} /></td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════
                DETAIL POPUP
            ══════════════════════════════════════════════════════════════ */}
            {popupOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
                    style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(6px)" }}>

                    <div className="relative flex flex-col rounded-2xl border border-slate-200 w-full max-w-4xl bg-white shadow-2xl"
                        style={{ maxHeight: "90vh", padding: "24px", boxShadow: "0 20px 60px rgba(15,23,42,0.18)" }}>

                        {/* Popup header */}
                        <div className="flex-shrink-0 flex flex-col md:flex-row md:items-center md:justify-between gap-3 pb-4 mb-4 border-b border-slate-100">
                            <div className="flex items-center gap-2.5">
                                <div className="w-1 h-6 rounded-full"
                                    style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                                <h2 className="text-base font-bold text-slate-800">
                                    {selectedEmployee?.employee_name || "Employee"}
                                    <span className="ml-2 text-xs font-medium text-slate-400">
                                        — {activeTab}
                                    </span>
                                </h2>
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                                <ExcelExport
                                    data={employeeDetails}
                                    columns={detailColumns}
                                    fileName={`Expense_Details_${selectedEmployee?.employee_id}_${selectedMonth}_${selectedYear}.xlsx`}
                                    reportTitle="Expense Detail Report"
                                    reportFilters={{ month: selectedMonth, year: selectedYear }}
                                />
                                <button
                                    onClick={() => updateAllRows("APPROVED")}
                                    disabled={employeeDetails.length === 0 || activeTab === "APPROVED" || activeTab === "REJECTED"}
                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors disabled:opacity-30"
                                >
                                    ✓ Approve All
                                </button>
                                <button
                                    onClick={() => updateAllRows("REJECTED")}
                                    disabled={employeeDetails.length === 0 || activeTab === "APPROVED" || activeTab === "REJECTED"}
                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold text-rose-700 border border-rose-200 bg-rose-50 hover:bg-rose-100 transition-colors disabled:opacity-30"
                                >
                                    ✕ Reject All
                                </button>
                            </div>
                        </div>

                        {/* Scrollable detail table */}
                        <div className="flex-1 overflow-auto exp-scrollbar rounded-xl border border-slate-200">
                            <table className="w-full text-sm min-w-[700px]">
                                <thead className="sticky top-0 z-10" style={{ background: "#f8fafc" }}>
                                    <tr>
                                        {["Date", "Type", "Amount", "Paid Amount", "Status", "Action"].map((h) => (
                                            <th key={h} className="px-3 py-3 text-center border-b border-slate-200">
                                                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</span>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {employeeDetails.length > 0 ? (
                                        employeeDetails.map((item, i) => (
                                            <tr key={item.id || i} className="border-t border-slate-100 hover:bg-blue-50/60 transition-colors text-center">
                                                <td className="px-3 py-2.5 text-slate-500 text-xs">{item.date}</td>
                                                <td className="px-3 py-2.5 text-slate-800 text-xs font-medium">{item.expense_type}</td>
                                                <td className="px-3 py-2.5 text-slate-700 text-xs">{Number(item.amount).toFixed(2)}</td>
                                                <td className="px-3 py-2.5 text-slate-700 text-xs">{Number(item.paid_amount).toFixed(2)}</td>
                                                <td className="px-3 py-2.5">
                                                    <StatusBadge status={normalizeStatus(item.status)} />
                                                </td>
                                                <td className="px-3 py-2.5">
                                                    {(normalizeStatus(item.status) === "PENDING" ||
                                                        normalizeStatus(item.status) === "PARTIALLY APPROVED") && (
                                                        <RowAction item={item} adminId={adminId} />
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={6} className="text-center py-8 text-slate-400 text-sm">No data available</td>
                                        </tr>
                                    )}

                                    {/* Partial approved totals */}
                                    {activeTab === "PARTIALLY APPROVED" && (
                                        <>
                                            <tr className="border-t border-slate-200 bg-amber-50">
                                                <td colSpan={2} className="text-right px-4 py-2.5 text-xs font-bold text-amber-700">Paid Total</td>
                                                <td colSpan={4} className="px-4 py-2.5 text-xs font-bold text-amber-700">{partialTotal.toFixed(2)}</td>
                                            </tr>
                                            <tr className="border-t border-slate-200 bg-rose-50">
                                                <td colSpan={2} className="text-right px-4 py-2.5 text-xs font-bold text-rose-700">Remaining</td>
                                                <td colSpan={4} className="px-4 py-2.5 text-xs font-bold text-rose-700">{remainingTotal.toFixed(2)}</td>
                                            </tr>
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer */}
                        <div className="flex-shrink-0 flex justify-end mt-4 pt-3 border-t border-slate-100">
                            <button
                                onClick={() => setPopupOpen(false)}
                                className="px-5 py-2 rounded-lg text-sm font-semibold text-slate-600 border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AdminExpenseApproval;